<template>
  <div class="dismissal-screen-v2">
    <div class="screen-container">
      <!-- 顶部标题栏：左、中、右极简布局 -->
      <div class="header-v2">
        <!-- 左侧：大屏终端名称 -->
        <div class="header-left">
          <span class="screen-name">{{ screenName || "大屏终端" }}</span>
        </div>

        <!-- 中间：学校大标题 -->
        <div class="header-center">
          <h1 class="school-title">{{ schoolName }}</h1>
        </div>

        <!-- 右侧：实时时间 -->
        <div class="header-right">
          <span class="time-clock">{{ currentTime }}</span>
        </div>
      </div>

      <!-- 顶部标题下方的放学中滚动条 -->
      <div class="marquee-bar-v2" :style="getMarqueeBarStyle()">
        <div class="marquee-label" :style="getMarqueeLabelStyle()">
          <span class="label-dot" :style="getMarqueeDotStyle()"></span>
          放学中
        </div>
        <div class="marquee-scroll-container" :class="{ 'is-scroll-centered': dismissingClasses.length <= 6 }">
          <div class="marquee-scroll-text" v-if="dismissingClasses.length > 0" :class="{ 'marquee-scroll-active': dismissingClasses.length > 6 }">
            <div class="marquee-content-inner">
              <span v-for="cls in dismissingClasses" :key="cls.ClassId" class="marquee-item" :style="getMarqueeItemStyle()">
                {{ cls.ClassName }}
              </span>
            </div>
            <!-- 复制一份实现无缝滚动（只有在激活滚动时才渲染，避免平铺多出一份） -->
            <div class="marquee-content-inner" v-if="dismissingClasses.length > 6">
              <span v-for="cls in dismissingClasses" :key="'dup-' + cls.ClassId" class="marquee-item" :style="getMarqueeItemStyle()">
                {{ cls.ClassName }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 班级列表大网格 -->
      <div class="main-content-v2">
        <div class="class-grid-section" ref="gridSection">
          <div class="grid-scroll-wrapper" ref="gridWrapper">
            <div class="grid-scroll-segment" ref="gridSegmentFirst">
              <div class="class-grid">
                <div
                  v-for="cls in sortedClasses"
                  :key="cls.ClassId"
                  class="class-card-v2"
                  :style="getCardStyle(cls.Status)"
                >
                  <div class="class-name-v2">{{ cls.ClassName }}</div>
                </div>
              </div>
            </div>
            <!-- 首尾分隔：班级过多滚动时，避免列表末尾与开头贴在一起 -->
            <div v-if="gridScrollActive" class="grid-loop-gap" ref="gridLoopGap" aria-hidden="true"></div>
            <div v-if="gridScrollActive" class="grid-scroll-segment">
              <div class="class-grid">
                <div
                  v-for="cls in sortedClasses"
                  :key="'dup-' + cls.ClassId"
                  class="class-card-v2"
                  :style="getCardStyle(cls.Status)"
                >
                  <div class="class-name-v2">{{ cls.ClassName }}</div>
                </div>
              </div>
            </div>
          </div>
          <!-- 暂无班级数据提示 -->
          <div v-if="sortedClasses.length === 0" class="empty-tip-v2">
            <div class="empty-icon"></div>
            暂无放学班级数据
          </div>
        </div>
      </div>

      <!-- 底部状态说明 -->
      <div class="bottom-legend-v2">
        <div class="legend-item">
          <span class="legend-dot" :style="getLegendDotStyle(0)"></span>
          <span class="legend-text" :style="getLegendTextStyle(0)">待放学</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot" :style="getLegendDotStyle(1)"></span>
          <span class="legend-text" :style="getLegendTextStyle(1)">放学中</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot" :style="getLegendDotStyle(2)"></span>
          <span class="legend-text" :style="getLegendTextStyle(2)">已放学</span>
        </div>
      </div>
    </div>

    <!-- 霸屏显示组件 (放学中班级 5 秒轮播霸屏) -->
    <div class="overlay-fullscreen" v-if="dismissingClasses.length > 0 && currentOverlayClass">
      <div class="overlay-backdrop"></div>
      <div class="overlay-content-box">
        <transition name="overlay-scale" mode="out-in">
          <div class="overlay-class-name" :key="currentOverlayClass.ClassId" :style="getOverlayClassStyle()">
            {{ currentOverlayClass.ClassName }}
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { getBigScreenData } from '../api/modules/big-screen';

export default {
  name: "DismissalBigScreenV2",
  props: {
    /** 父组件注入的大屏数据（Auto 页使用，避免重复轮询） */
    externalData: { type: Object, default: null },
    /**
     * 为 true 时不自行请求接口，仅消费 externalData。
     * 生产路由 /DismissalBigScreen 由 DismissalBigScreenAuto 挂载本组件并设 skipFetch=true；
     * 轻量模式 light 由后端 GetDismissalBigScreenAutoData 内部传入，子组件无需再传。
     */
    skipFetch: { type: Boolean, default: false }
  },
  data() {
    return {
      // 实时时间字符串
      currentTime: "",
      // 时间刷新定时器
      timer: null,
      // 接口数据轮询定时器
      dataTimer: null,
      // 学校名称
      schoolName: "智慧校园",
      // 大屏终端名称
      screenName: "",
      // 原始年级班级数据
      gradeData: [],
      // 扁平化并排序后的所有班级数据
      sortedClasses: [],
      // 网格滚动激活状态
      gridScrollActive: false,
      // 缓存的单轮滚动高度（从 DOM 读取后保存，避免每帧 reflow）
      cachedLoopHeight: 0,
      // requestAnimationFrame 句柄（取代 setInterval，对 CPU 更友好）
      gridScrollRAF: null,
      // 霸屏切换定时器（语音播完后延迟切换，非固定 interval）
      overlayAdvanceTimer: null,
      // 已展示过霸屏的班级 ID 记录
      shownOverlayClassIds: [],
      // 加载状态
      loading: false,
      // 这一轮已播放过语音的班级 ID 列表，只增不减以确保一个班级只播放一次，放学完毕后自动清空
      hasSpokenClassIds: [],
      // 延迟重置已播语音白名单的防抖定时器，用于兼容多次放学及防接口抖动
      resetTimer: null,
      // 模拟数据：URL 加 ?mock=1 启用（如 /DismissalBigScreen?mock=1）
      isMock: false,
      // 状态颜色配置，支持后台修改并提供默认回退
      pendingColor: "blue",
      dismissingColor: "red",
      finishedColor: "green",
      // 语音播报次数，由后台配置决定，默认 2 次
      voiceRepeatCount: 2,
      // 语音播报文案后缀，拼接在班级名称之后
      voiceSuffix: "正在放学，请家长在等候区等待",
      // 语音是否已解锁（Speech API 可用且已成功播报过）
      voiceUnlocked: false,
      // 后台自动重试解锁语音
      voiceUnlockRetryTimer: null,
      // 当前班级语音播报失败后的自动重试
      speakRetryTimer: null,
      // 正在播报语音的班级 ID（标准化字符串），用于防止接口刷新重复 cancel
      speakingClassId: null,
      // 语音播报超时兜底，防止 onend 未触发导致霸屏不消失
      overlaySafetyTimer: null,
      // 霸屏最长展示定时器（不因语音重试而重置，解决单班卡死）
      overlayForceHideTimer: null,
      // Chrome 已知 bug：长时间运行后 speechSynthesis 会暂停，需定期 resume
      speechResumeTimer: null
    };
  },
  computed: {
    // 过滤出当前处于“放学中”状态 of 班级
    dismissingClasses() {
      return this.sortedClasses.filter(c => c.Status === 1);
    },
    // 当前需要霸屏显示的班级（仅显示未曾展示过霸屏的班级，且全部显示完后隐去）
    currentOverlayClass() {
      const pending = this.dismissingClasses.filter(c =>
        !this.shownOverlayClassIds.some(id => this.normalizeClassId(id) === this.normalizeClassId(c.ClassId))
      );
      return pending.length > 0 ? pending[0] : null;
    },
  },
  watch: {
    externalData: {
      handler(val) {
        if (val && this.skipFetch && !this.isMock) {
          this.applyData(val);
        }
      },
      deep: true
    },
    // 仅 ClassId 变化时触发播报，避免接口刷新重建对象导致重复 speak / cancel
    currentOverlayClass: {
      handler(newVal, oldVal) {
        const newId = newVal ? this.normalizeClassId(newVal.ClassId) : "";
        const oldId = oldVal ? this.normalizeClassId(oldVal.ClassId) : "";
        if (newVal && newId !== oldId) {
          this.startOverlayForceHideTimer(newVal.ClassName, newVal.ClassId);
          this.speakDismissal(newVal.ClassName, newVal.ClassId);
        } else if (!newVal && this.speakingClassId) {
          // 当前播报班级已离开放学队列（如老师取消了放学），立即停止语音并清理状态
          if (window.speechSynthesis) window.speechSynthesis.cancel();
          this.clearOverlaySafetyTimer();
          this.speakingClassId = null;
        }
      },
      immediate: true
    },
    // 监听放学中的班级变化，动态调整已霸屏记录
    dismissingClasses: {
      handler(newVal) {
        if (!newVal || newVal.length === 0) {
          // 全部放学结束：清空霸屏记录，但不清已播语音白名单（由 resetTimer 1 分钟后延迟清理）
          // ⚠️ 不在这里 clearOverlayTimer，避免清掉当前正在倒计时的 advance/safety timer
          this.shownOverlayClassIds = [];
          this.clearOverlayForceHideTimer();

          if (!this.resetTimer) {
            this.resetTimer = setTimeout(() => {
              this.hasSpokenClassIds = [];
              this.resetTimer = null;
            }, 60000);
          }
        } else {
          // 有放学中的班级，撤销 1 分钟重置定时器
          if (this.resetTimer) {
            clearTimeout(this.resetTimer);
            this.resetTimer = null;
          }

          // 仅清理已离开 dismissingClasses（已完成放学）的已展示记录，
          // 避免 currentOverlayClass 产生无意义回跳
          const activeIds = new Set(newVal.map(c => this.normalizeClassId(c.ClassId)));
          this.shownOverlayClassIds = this.shownOverlayClassIds.filter(id =>
            activeIds.has(this.normalizeClassId(id))
          );

          // ⚠️ 不调用 startOverlayTimer/clearOverlayTimer：
          // advance/safety timer 由 speakDismissal 独立管理，接口轮询不应干预
        }
      },
      deep: true
    }
  },
  mounted() {
    const mockQuery = this.$route.query.mock;
    this.isMock = mockQuery === "1" || mockQuery === "true" || mockQuery === "yes";
    this.updateTime();
    this.timer = setInterval(this.updateTime, 1000);
    if (this.isMock) {
      // mock 模式优先：即使被 Auto 页 skipFetch 包裹，也走本地模拟数据
      this.fetchData();
    } else if (!this.skipFetch) {
      this.fetchData();
      // 每 5 秒轮询拉取最新数据
      this.dataTimer = setInterval(this.fetchData, 5000);
    } else if (this.externalData) {
      this.applyData(this.externalData);
    }
    // 预加载语音列表并后台自动重试解锁，无需用户点击
    this.initSpeechSynthesis();
    this.$nextTick(() => {
      this.tryAutoUnlockVoice();
      this.startVoiceUnlockRetry();
    });
    // Chrome 已知 bug：speechSynthesis 长时间运行（约 15 分钟）后会自动暂停，
    // onend 永不触发，导致语音永久卡死。每 10 秒检查一次，若已暂停则 resume
    if (window.speechSynthesis) {
      this.speechResumeTimer = setInterval(() => {
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
      }, 10000);
    }
    // 开启网格平滑滚动
    this.startGridScroll();

    // 监听窗口大小变化以重新计算滚动状态
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (this.dataTimer) clearInterval(this.dataTimer);
    this.stopGridScroll();
    this.clearOverlayTimer();
    window.removeEventListener("resize", this.handleResize);
    // 清理防抖重置定时器
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
    this.stopVoiceUnlockRetry();
    if (this.speakRetryTimer) {
      clearTimeout(this.speakRetryTimer);
      this.speakRetryTimer = null;
    }
    this.speakingClassId = null;
    if (this.overlaySafetyTimer) {
      clearTimeout(this.overlaySafetyTimer);
      this.overlaySafetyTimer = null;
    }
    this.clearOverlayForceHideTimer();
    if (this.speechResumeTimer) {
      clearInterval(this.speechResumeTimer);
      this.speechResumeTimer = null;
    }
    // 离开页面时取消所有正在播放 and 排队的语音
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  },
  methods: {
    /** 统一班级 ID 为字符串，避免 number/string 混用导致白名单失效 */
    normalizeClassId(classId) {
      if (classId == null) return "";
      return String(classId);
    },
    // 更新当前时间
    updateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      
      const weeks = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
      const week = weeks[now.getDay()];
      
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      
      this.currentTime = `${year}-${month}-${day} ${week} ${hours}:${minutes}`;
    },
    // 映射状态 class 样式
    statusClass(status) {
      if (status === 2) return "card-finished-v2"; // 已放学 - 绿色
      if (status === 1) return "card-dismissing-v2"; // 放学中 - 黄色/橙色
      return "card-pending-v2"; // 未放学 - 蓝色
    },
    /** 将接口或父组件数据应用到视图 */
    applyData(data) {
      if (!data) return;
      this.schoolName = data.SchoolName || "智慧校园";
      this.screenName = data.ScreenName || "南门大屏";
      this.gradeData = data.GradeData || [];

      if (data.PendingColor) this.pendingColor = data.PendingColor;
      if (data.DismissingColor) this.dismissingColor = data.DismissingColor;
      if (data.FinishedColor) this.finishedColor = data.FinishedColor;
      if (data.VoiceRepeatCount >= 1) this.voiceRepeatCount = data.VoiceRepeatCount;
      if (data.VoiceSuffix) this.voiceSuffix = data.VoiceSuffix;

      this.processAndSortClasses();

      this.$nextTick(() => {
        this.checkGridScroll();
      });
    },
    // 拉取大屏数据（?mock=1 时走本地模拟，不请求接口）
    fetchData() {
      if (this.isMock) {
        this.applyMockScreenMeta();
        this.processAndSortClasses();
        this.loading = false;
        this.$nextTick(() => {
          this.checkGridScroll();
        });
        return;
      }

      const code = this.$route.query.code || "";
      this.loading = true;
      // 独立轮询场景（非 Auto 嵌入）走轻量接口；生产主路径见 props.skipFetch 说明
      getBigScreenData({ CODE: code, light: 1 })
        .then(res => {
          if (res.data && res.data.success) {
            this.applyData(res.data.response);
          }
          this.loading = false;
        })
        .catch(() => {
          // 请求失败保留上一次数据，不切换为模拟数据（避免生产大屏播报假班级）
          this.loading = false;
        });
    },
    /** 模拟大屏页眉信息 */
    applyMockScreenMeta() {
      this.schoolName = "临海市哲商现代实验小学";
      this.screenName = "西校区-五年级";
      this.pendingColor = "blue";
      this.dismissingColor = "red";
      this.finishedColor = "green";
      this.gradeData = [];
    },
    /** 生成较多班级，用于测试网格滚动与首尾间隔 */
    buildMockClassList() {
      const list = [];
      let globalIndex = 0;
      const grades = ["一年级", "二年级", "三年级", "四年级", "五年级", "六年级"];
      const classesPerGrade = 10;

      grades.forEach(gradeName => {
        for (let classNum = 1; classNum <= classesPerGrade; classNum++) {
          const className = `${gradeName}${classNum}班`;
          const classId = `mock-${gradeName}-${classNum}`;
          let status = 0;
          if (gradeName === "一年级") {
            status = 2;
          } else if (gradeName === "二年级" || (gradeName === "三年级" && classNum <= 2)) {
            status = 1;
          }

          list.push({
            ClassId: classId,
            ClassName: className,
            Status: status,
            gradeName,
            originalIndex: globalIndex++
          });
        }
      });

      return list;
    },
    // 将分组班级扁平化，并按照 已放学(2) -> 放学中(1) -> 未放学(0) 进行全局排序
    // 双轨制兼容：根据 data 中的 isMock 调试开关判定是否强制启用 36 个不重复班级的模拟数据
    processAndSortClasses() {
      let list = [];
      let globalIndex = 0;

      // 1. 尝试解析并扁平化真实接口返回的年级班级数据 (未开启 data 里的 isMock 且 gradeData 有数据时才使用真实数据)
      if (!this.isMock && this.gradeData && this.gradeData.length > 0) {
        this.gradeData.forEach(grade => {
          if (grade.Classes) {
            grade.Classes.forEach(cls => {
              list.push({
                ...cls,
                gradeName: grade.GradeName,
                originalIndex: globalIndex++
              });
            });
          }
        });
      }

      // 2. 仅 ?mock=1 时使用 60 个班模拟数据；接口无数据时保持空列表，显示「暂无放学班级数据」
      if (this.isMock) {
        list = this.buildMockClassList();
      }

      // 重新排序：已放学(2) > 放学中(1) > 待放学(0)
      list.sort((a, b) => {
        if (b.Status !== a.Status) {
          return b.Status - a.Status;
        }
        return a.originalIndex - b.originalIndex;
      });

      // 实时清理已播放语音白名单：
      // 如果某个班级在最新数据中的状态不再是 1 (放学中)，说明其上一轮放学流程已结束（变为了已放学或待放学）。
      // 此时将其从已播白名单 hasSpokenClassIds 中移除，这样如果教师在后台重新点击该班级放学，它能再次触发播报。
      list.forEach(cls => {
        if (cls.Status !== 1) {
          const normalizedId = this.normalizeClassId(cls.ClassId);
          const idx = this.hasSpokenClassIds.indexOf(normalizedId);
          if (idx > -1) {
            this.hasSpokenClassIds.splice(idx, 1);
          }
          if (this.speakingClassId === normalizedId) {
            this.speakingClassId = null;
          }
        }
      });

      this.sortedClasses = list;
    },
    // 检查网格是否超出容器高度，从而决定是否启动滚动
    // ⚠️ 此处允许读取 DOM 尺寸（只在数据变化/resize 时调用，不在每帧调用）
    checkGridScroll() {
      const section = this.$refs.gridSection;
      if (!section) return;

      const containerHeight = section.clientHeight;
      this.gridScrollActive = false;

      this.$nextTick(() => {
        const first = this.$refs.gridSegmentFirst;
        const firstHeight = first ? (first.offsetHeight || first.scrollHeight) : 0;
        if (firstHeight > containerHeight) {
          this.gridScrollActive = true;
          this.$nextTick(() => {
            // 在 gridScrollActive=true 后（复制段已渲染），读取并缓存循环高度
            this.cachedLoopHeight = this.getGridLoopHeight();
            if (this.cachedLoopHeight <= containerHeight) {
              this.gridScrollActive = false;
              this.cachedLoopHeight = 0;
              const wrapper = this.$refs.gridWrapper;
              if (wrapper) wrapper.style.transform = "translateY(0)";
            }
          });
        } else {
          this.cachedLoopHeight = 0;
          const wrapper = this.$refs.gridWrapper;
          if (wrapper) wrapper.style.transform = "translateY(0)";
        }
      });
    },
    // 单轮滚动高度：首段列表 + 首尾间隔（与无缝复位点对齐）
    getGridLoopHeight() {
      const first = this.$refs.gridSegmentFirst;
      if (!first) return 0;
      const gap = this.$refs.gridLoopGap;
      const gapHeight = gap ? gap.offsetHeight : 0;
      return (first.offsetHeight || first.scrollHeight) + gapHeight;
    },
    // 启动垂直网格滚动（requestAnimationFrame + 直接操作 DOM，不走 Vue 响应式）
    startGridScroll() {
      if (this.gridScrollRAF) cancelAnimationFrame(this.gridScrollRAF);
      let scrollY = 0;
      let lastTs = null;
      const speed = 40; // px/秒

      const tick = (ts) => {
        if (lastTs !== null) {
          // 与宿舍大屏一致：鼠标悬停不暂停，保证大屏持续滚动
          if (this.gridScrollActive) {
            const delta = ts - lastTs;
            scrollY += (speed * delta) / 1000;

            const loopH = this.cachedLoopHeight;
            if (loopH > 0 && scrollY >= loopH) {
              scrollY -= loopH; // 无缝循环：超出一轮后折回，不闪烁
            }

            const wrapper = this.$refs.gridWrapper;
            if (wrapper) wrapper.style.transform = `translateY(-${scrollY}px)`;
          }
        }
        lastTs = ts;
        this.gridScrollRAF = requestAnimationFrame(tick);
      };

      this.gridScrollRAF = requestAnimationFrame(tick);
    },
    stopGridScroll() {
      if (this.gridScrollRAF) {
        cancelAnimationFrame(this.gridScrollRAF);
        this.gridScrollRAF = null;
      }
    },
    // 启动霸屏轮播：切换时机由语音播完后推进，不再使用固定 4 秒 interval
    // ⚠️ 不能在这里 clearOverlayTimer——接口 5s 轮询会反复调用此方法，
    // 若清掉 overlayAdvanceTimer / overlaySafetyTimer 会导致语音播完后霸屏永久卡死
    startOverlayTimer() {},
    clearOverlayAdvanceTimer() {
      if (this.overlayAdvanceTimer) {
        clearTimeout(this.overlayAdvanceTimer);
        this.overlayAdvanceTimer = null;
      }
    },
    /** 该班级是否已在霸屏队列中展示过 */
    isOverlayClassShown(classId) {
      const normalizedId = this.normalizeClassId(classId);
      return this.shownOverlayClassIds.some(id => this.normalizeClassId(id) === normalizedId);
    },
    /** 标记班级霸屏已展示，currentOverlayClass 会自动切到下一班或为空 */
    markOverlayClassShown(classId) {
      const normalizedId = this.normalizeClassId(classId);
      if (!this.isOverlayClassShown(normalizedId)) {
        this.shownOverlayClassIds.push(normalizedId);
      }
      if (!this.currentOverlayClass) {
        this.clearOverlayForceHideTimer();
      }
    },
    clearOverlaySafetyTimer() {
      if (this.overlaySafetyTimer) {
        clearTimeout(this.overlaySafetyTimer);
        this.overlaySafetyTimer = null;
      }
    },
    clearOverlayForceHideTimer() {
      if (this.overlayForceHideTimer) {
        clearTimeout(this.overlayForceHideTimer);
        this.overlayForceHideTimer = null;
      }
    },
    /**
     * 霸屏最长展示时间（与语音重试无关，只在新班级出现时启动一次）。
     * 解决：仅一个班放学时语音解锁反复重试导致 safety timer 被不断重置、红色大字长期不消失。
     */
    startOverlayForceHideTimer(className, classId) {
      this.clearOverlayForceHideTimer();
      const normalizedId = this.normalizeClassId(classId);
      const suffix = this.voiceSuffix || "正在放学，请家长在等候区等待";
      const text = `${className}${suffix}`;
      const repeatCount = this.voiceRepeatCount >= 1 ? this.voiceRepeatCount : 2;
      const maxMs = this.estimateVoiceDurationMs(text, repeatCount) + 3000;
      this.overlayForceHideTimer = setTimeout(() => {
        this.overlayForceHideTimer = null;
        if (!this.isOverlayClassShown(normalizedId)) {
          if (window.speechSynthesis) window.speechSynthesis.cancel();
          if (this.speakingClassId === normalizedId) this.speakingClassId = null;
          this.clearOverlaySafetyTimer();
          this.clearOverlayAdvanceTimer();
          if (!this.hasSpokenClassIds.includes(normalizedId)) {
            this.hasSpokenClassIds.push(normalizedId);
          }
          this.markOverlayClassShown(normalizedId);
        }
      }, maxMs);
    },
    /** 语音播完后延迟标记该班已展示（按 classId，不依赖 currentOverlayClass 是否变化） */
    scheduleAdvanceOverlayForClass(classId, delayMs) {
      this.clearOverlayAdvanceTimer();
      const normalizedId = this.normalizeClassId(classId);
      this.overlayAdvanceTimer = setTimeout(() => {
        this.overlayAdvanceTimer = null;
        this.markOverlayClassShown(normalizedId);
      }, delayMs);
    },
    /** 估算语音时长（毫秒），用于无法播报时的兜底等待 */
    estimateVoiceDurationMs(text, repeatCount) {
      const perPlayMs = Math.max(3500, (text.length / 3.2) * 1000);
      return Math.ceil(perPlayMs * repeatCount + 800);
    },
    clearOverlayTimer() {
      this.clearOverlayAdvanceTimer();
      this.clearOverlaySafetyTimer();
      this.clearOverlayForceHideTimer();
    },
    /** 预加载 TTS 语音列表，voices 就绪后再尝试解锁 */
    initSpeechSynthesis() {
      if (!window.speechSynthesis) return;
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        if (!this.voiceUnlocked) {
          this.tryAutoUnlockVoice();
        }
      };
    },
    startVoiceUnlockRetry() {
      if (this.voiceUnlockRetryTimer || this.voiceUnlocked) return;
      this.voiceUnlockRetryTimer = setInterval(() => {
        if (this.voiceUnlocked) {
          this.stopVoiceUnlockRetry();
          return;
        }
        this.tryAutoUnlockVoice();
      }, 2000);
    },
    stopVoiceUnlockRetry() {
      if (this.voiceUnlockRetryTimer) {
        clearInterval(this.voiceUnlockRetryTimer);
        this.voiceUnlockRetryTimer = null;
      }
    },
    /** 语音解锁成功后，补播当前霸屏班级 */
    onVoiceUnlocked() {
      if (this.voiceUnlocked) return;
      this.voiceUnlocked = true;
      this.stopVoiceUnlockRetry();
      const current = this.currentOverlayClass;
      if (current) {
        const classId = this.normalizeClassId(current.ClassId);
        // 解锁前可能已标记跳过，允许重新播报
        this.hasSpokenClassIds = this.hasSpokenClassIds.filter(id => id !== classId);
        this.speakDismissal(current.ClassName, current.ClassId);
      }
    },
    /** 播报失败时自动重试当前班级 */
    scheduleSpeakRetry(className, classId) {
      if (this.speakRetryTimer) clearTimeout(this.speakRetryTimer);
      this.speakRetryTimer = setTimeout(() => {
        this.speakRetryTimer = null;
        if (this.currentOverlayClass && this.normalizeClassId(this.currentOverlayClass.ClassId) === this.normalizeClassId(classId)) {
          this.tryAutoUnlockVoice();
          this.speakDismissal(className, classId);
        }
      }, 1500);
    },
    // 静默预热 Speech API；成功则标记解锁并补播当前班级
    tryAutoUnlockVoice() {
      if (!window.speechSynthesis) {
        this.onVoiceUnlocked();
        return;
      }
      if (this.voiceUnlocked) return;
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      u.lang = "zh-CN";
      u.onstart = () => { this.onVoiceUnlocked(); };
      u.onend = () => { this.onVoiceUnlocked(); };
      u.onerror = () => {};
      window.speechSynthesis.speak(u);
    },
    // 播报正在放学的班级语音，播完配置次数后再切换下一个霸屏班级
    speakDismissal(className, classId) {
      const normalizedId = this.normalizeClassId(classId);
      if (!this.currentOverlayClass || this.normalizeClassId(this.currentOverlayClass.ClassId) !== normalizedId) {
        return;
      }
      // 同一班级正在播报中（含多遍连播），接口刷新等重复触发直接忽略
      if (this.speakingClassId === normalizedId) {
        if (this.hasSpokenClassIds.includes(normalizedId) && !this.isOverlayClassShown(normalizedId)) {
          this.scheduleAdvanceOverlayForClass(normalizedId, 800);
        }
        return;
      }
      // 本轮已完整播过，不再重播；若霸屏未标记已展示则补推进（防止卡死在红色霸屏）
      if (this.hasSpokenClassIds.includes(normalizedId)) {
        if (!this.isOverlayClassShown(normalizedId)) {
          this.scheduleAdvanceOverlayForClass(normalizedId, 800);
        }
        return;
      }

      this.clearOverlayAdvanceTimer();

      const suffix = this.voiceSuffix || "正在放学，请家长在等候区等待";
      const text = `${className}${suffix}`;
      const repeatCount = this.voiceRepeatCount >= 1 ? this.voiceRepeatCount : 2;
      const fallbackDelay = this.estimateVoiceDurationMs(text, repeatCount);

      const scheduleAfterVoice = (delayMs) => {
        this.clearOverlaySafetyTimer();
        this.scheduleAdvanceOverlayForClass(normalizedId, delayMs);
      };

      const finishSpeaking = () => {
        if (this.speakingClassId === normalizedId) {
          this.speakingClassId = null;
        }
        this.clearOverlaySafetyTimer();
      };

      if (!window.speechSynthesis) {
        console.warn("当前浏览器不支持 Web Speech API 语音合成");
        this.hasSpokenClassIds.push(normalizedId);
        scheduleAfterVoice(fallbackDelay);
        return;
      }

      // 语音已解锁时，无论 speakingClassId 是否有值，都 cancel 旧队列再播新班
      // （safety timer 触发后 speakingClassId 会被清空，但 TTS 可能仍卡在队列里）
      if (this.voiceUnlocked) {
        window.speechSynthesis.cancel();
      }
      this.speakingClassId = normalizedId;

      // 超时兜底：onend 未触发时仍标记霸屏已展示，避免红色大字一直不消失
      this.clearOverlaySafetyTimer();
      this.overlaySafetyTimer = setTimeout(() => {
        this.overlaySafetyTimer = null;
        if (!this.isOverlayClassShown(normalizedId)) {
          // 强制 cancel 可能卡住的 TTS，防止画面切到下一班后仍播旧班语音
          if (window.speechSynthesis) window.speechSynthesis.cancel();
          finishSpeaking();
          this.scheduleAdvanceOverlayForClass(normalizedId, 0);
        }
      }, fallbackDelay + 2000);

      let playCount = 0;
      let markedSpoken = false;
      const markSpokenOnce = () => {
        if (markedSpoken) return;
        markedSpoken = true;
        if (!this.hasSpokenClassIds.includes(normalizedId)) {
          this.hasSpokenClassIds.push(normalizedId);
        }
      };

      const play = () => {
        const u = new SpeechSynthesisUtterance(text);
        u.lang = "zh-CN";
        u.volume = 1.0; // Web Speech API 最大音量（0~1）
        u.rate = 0.9;
        u.pitch = 1.0;
        u.onstart = () => {
          if (!this.voiceUnlocked) {
            this.voiceUnlocked = true;
            this.stopVoiceUnlockRetry();
          }
          markSpokenOnce();
        };
        u.onend = () => {
          markSpokenOnce();
          playCount++;
          if (playCount < repeatCount) {
            play();
          } else {
            finishSpeaking();
            scheduleAfterVoice(800);
          }
        };
        u.onerror = () => {
          markSpokenOnce();
          finishSpeaking();
          if (!this.voiceUnlocked) {
            this.scheduleSpeakRetry(className, classId);
          }
          // 无论语音是否解锁成功，都安排霸屏退场，避免单班红色大字长期霸屏
          if (!this.isOverlayClassShown(normalizedId)) {
            scheduleAfterVoice(fallbackDelay);
          }
        };
        window.speechSynthesis.speak(u);
      };
      play();
    },
    // 处理窗口尺寸调整
    handleResize() {
      this.checkGridScroll();
    },
    // 获取指定状态的颜色名并处理兼容 (若未配置则回退到推荐默认色)
    getStateColorHex(status) {
      let color = "";
      if (status === 0) color = this.pendingColor || "#3b82f6";
      else if (status === 1) color = this.dismissingColor || "#ef4444";
      else if (status === 2) color = this.finishedColor || "#10b981";
      
      const colorDict = {
        green: "#10b981",
        darkgreen: "#047857",
        blue: "#3b82f6",
        darkblue: "#1d4ed8",
        black: "#1e293b",
        gold: "#eab308",
        red: "#ef4444"
      };
      
      if (colorDict[color]) {
        return colorDict[color];
      }
      return color;
    },
    // 解析十六进制颜色为 RGB，供发光特效使用
    hexToRgbStr(hex) {
      if (!hex) return "59, 130, 246";
      let s = hex.replace("#", "");
      if (s.length === 3) {
        s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
      }
      const num = parseInt(s, 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `${r}, ${g}, ${b}`;
    },
    // 动态调整十六进制颜色亮度
    adjustColorBrightness(hex, percent) {
      let s = hex.replace("#", "");
      if (s.length === 3) {
        s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
      }
      let num = parseInt(s, 16);
      let r = (num >> 16) & 255;
      let g = (num >> 8) & 255;
      let b = num & 255;

      r = Math.min(255, Math.max(0, parseInt(r * (100 + percent) / 100)));
      g = Math.min(255, Math.max(0, parseInt(g * (100 + percent) / 100)));
      b = Math.min(255, Math.max(0, parseInt(b * (100 + percent) / 100)));

      return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    },
    // 动态卡片背景样式 (立体微渐变)
    getCardStyle(status) {
      const color = this.getStateColorHex(status);
      const darkColor = this.adjustColorBrightness(color, -25);
      return {
        background: `linear-gradient(180deg, ${color} 0%, ${darkColor} 100%)`,
        border: '1px solid rgba(255, 255, 255, 0.1)'
      };
    },
    // 动态指示灯水晶球样式 (径向立体渐变)
    getLegendDotStyle(status) {
      const color = this.getStateColorHex(status);
      const lightColor = this.adjustColorBrightness(color, 40);
      const darkColor = this.adjustColorBrightness(color, -35);
      const rgb = this.hexToRgbStr(color);
      return {
        background: `radial-gradient(circle at 35% 35%, ${lightColor} 0%, ${color} 60%, ${darkColor} 100%)`,
        boxShadow: `0 0 12px rgba(${rgb}, 0.85)`,
        '--pulse-rgb': rgb
      };
    },
    // 动态图例文字样式（固定 px，兼容电视机 WebView 不支持 clamp/vw 的场景）
    getLegendTextStyle(status) {
      const color = this.getStateColorHex(status);
      return {
        color: color,
        fontWeight: status === 1 ? '800' : '700',
        fontSize: '40px'
      };
    },
    // 动态跑马灯容器边框样式
    getMarqueeBarStyle() {
      const color = this.getStateColorHex(1);
      const rgb = this.hexToRgbStr(color);
      return {
        borderColor: `rgba(${rgb}, 0.3) !important`,
        backgroundColor: `rgba(${rgb}, 0.05) !important`
      };
    },
    // 动态跑马灯标签样式
    getMarqueeLabelStyle() {
      const color = this.getStateColorHex(1);
      const rgb = this.hexToRgbStr(color);
      return {
        color: `${color} !important`,
        backgroundColor: `rgba(${rgb}, 0.08) !important`,
        borderColor: `rgba(${rgb}, 0.25) !important`
      };
    },
    // 动态跑马灯指示圆点样式
    getMarqueeDotStyle() {
      if (this.dismissingClasses.length === 0) return {};
      const color = this.getStateColorHex(1);
      const rgb = this.hexToRgbStr(color);
      const lightColor = this.adjustColorBrightness(color, 40);
      const darkColor = this.adjustColorBrightness(color, -35);
      return {
        background: `radial-gradient(circle at 35% 35%, ${lightColor} 0%, ${color} 60%, ${darkColor} 100%)`,
        boxShadow: `0 0 8px rgba(${rgb}, 0.85)`,
        '--pulse-rgb': rgb
      };
    },
    // 动态跑马灯文字样式
    getMarqueeItemStyle() {
      const color = this.getStateColorHex(1);
      return {
        color: `${color} !important`
      };
    },
    // 动态霸屏超大字发光投影
    getOverlayClassStyle() {
      const color = this.getStateColorHex(1);
      const rgb = this.hexToRgbStr(color);
      return {
        color: `${color} !important`,
        textShadow: `0 4px 20px rgba(0, 0, 0, 0.8), 0 0 45px rgba(${rgb}, 0.85)`
      };
    },
    // 将十六进制颜色解析为 RGB 数组，以进行色彩智能匹配
    hexToRgbArray(hex) {
      if (!hex) return [0, 0, 0];
      let s = hex.replace("#", "");
      if (s.length === 3) {
        s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
      }
      const num = parseInt(s, 16);
      return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
    },
    // 获取颜色对应的中文名展示，支持智能 RGB 空间最邻近算法匹配中文名
    getLegendText(status) {
      const color = this.getStateColorHex(status);
      
      // 精美中文传统及常见色库
      const colorMap = [
        { name: "天蓝色", hex: "#3b82f6", rgb: [59, 130, 246] },
        { name: "深蓝色", hex: "#1d4ed8", rgb: [29, 78, 216] },
        { name: "中国红", hex: "#e60012", rgb: [230, 0, 18] },
        { name: "鲜红色", hex: "#ef4444", rgb: [239, 68, 68] },
        { name: "翡翠绿", hex: "#10b981", rgb: [16, 185, 129] },
        { name: "深绿色", hex: "#047857", rgb: [4, 120, 87] },
        { name: "金黄色", hex: "#eab308", rgb: [234, 179, 8] },
        { name: "咖啡棕", hex: "#8b5a2b", rgb: [139, 90, 43] },
        { name: "暗黑色", hex: "#1e293b", rgb: [30, 41, 59] },
        { name: "玫瑰粉", hex: "#ec4899", rgb: [236, 72, 153] },
        { name: "紫罗兰", hex: "#8b5cf6", rgb: [139, 92, 246] },
        { name: "薄荷绿", hex: "#14b8a6", rgb: [20, 184, 166] },
        { name: "珊瑚橙", hex: "#f97316", rgb: [249, 115, 22] },
        { name: "纯白色", hex: "#ffffff", rgb: [255, 255, 255] }
      ];

      // 1. 优先进行精确匹配
      const exactMatch = colorMap.find(c => c.hex.toLowerCase() === color.toLowerCase());
      if (exactMatch) {
        return exactMatch.name;
      }

      // 2. 自定义色值智能计算 RGB 距离，匹配最近的精美中文色名
      const targetRgb = this.hexToRgbArray(color);
      let minDistance = Infinity;
      let closestColorName = "自定义";

      colorMap.forEach(c => {
        const dist = Math.pow(targetRgb[0] - c.rgb[0], 2) +
                     Math.pow(targetRgb[1] - c.rgb[1], 2) +
                     Math.pow(targetRgb[2] - c.rgb[2], 2);
        if (dist < minDistance) {
          minDistance = dist;
          closestColorName = c.name;
        }
      });

      return closestColorName;
    }
  }
};
</script>

<style scoped>
/* 根容器：极简科技感深色星空渐变 */
.dismissal-screen-v2 {
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at 50% 40%, #082d6e 0%, #03112b 100%); /* 改为高通透的科技感靛蓝渐变底色 */
  color: #e2e8f0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  overflow: hidden;
  box-sizing: border-box;
  padding: 16px;
  position: relative;
}

/* 简约大方的大框容器 */
.screen-container {
  width: 100%;
  height: 100%;
  border: 1.5px solid rgba(0, 168, 255, 0.4); /* 青蓝色亮丽边框 */
  border-radius: 12px;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.6), 
    inset 0 0 20px rgba(0, 168, 255, 0.15); /* 亮蓝色发光科技质感 */
  background: rgba(8, 23, 56, 0.4); /* 替换原本暗色背景，使之通透 */
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* 顶部标题栏：水平对称、扁平极简布局 */
.header-v2 {
  height: 76px;
  min-height: 76px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  background: transparent; /* 去除原本灰黑色遮罩，保持清亮 */
  border-bottom: 1px solid rgba(0, 168, 255, 0.2); /* 青蓝高对比细下线 */
  z-index: 10;
  position: relative;
}

/* 左、中、右文本对齐 */
.header-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.header-center {
  flex: 2;
  display: flex;
  justify-content: center;
  align-items: center;
}

.header-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* 大屏终端名称 */
.screen-name {
  font-size: 28px;
  font-weight: 700;
  color: #ffffff; /* 调亮至纯白，确保在深色背景下清晰可见 */
  letter-spacing: 1px;
}

/* 实时时间 */
.time-clock {
  font-size: 28px;
  font-weight: 700;
  font-family: Consolas, Monaco, monospace;
  color: #ffffff; /* 调亮至纯白，确保在深色背景下清晰可见 */
}

/* 现代大气学校大标题 */
.school-title {
  margin: 0;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: 4px;
  color: #ffffff;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
}

/* 放学中横向滚动跑马灯公告栏 */
.marquee-bar-v2 {
  width: calc(100% - 80px); /* 宽度缩减，左右留空对齐 */
  margin: 16px auto 0 auto; /* 独立悬浮，顶边留间距 */
  height: 80px; /* 从 64px 调大到 80px 提升饱满度 */
  min-height: 80px;
  display: flex;
  align-items: center;
  background: rgba(0, 168, 255, 0.05); /* 极淡半透明底色，防重沉感 */
  border: 1.5px solid rgba(0, 168, 255, 0.3); /* 青蓝色描边 */
  border-radius: 8px; /* 圆角独立盒子化 */
  padding: 0 40px;
  box-sizing: border-box;
  overflow: hidden;
  z-index: 9;
}

/* 放学中标签，调大字号更醒目 */
.marquee-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 26px; /* 从 22px 调大到 26px */
  font-weight: 800;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
  padding: 8px 20px; /* 增加内边距 */
  border-radius: 6px;
  border: 1px solid rgba(239, 68, 68, 0.25);
  margin-right: 24px;
  flex-shrink: 0;
}

/* 精致状态指示灯 */
.label-dot {
  width: 12px; /* 从 10px 调大到 12px */
  height: 12px; /* 从 10px 调大到 12px */
  background-color: #475569;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.label-dot.dot-active {
  background-color: #ef4444;
  box-shadow: 0 0 8px #ef4444;
  animation: beacon-blink 1s infinite alternate;
}

@keyframes beacon-blink {
  0% {
    opacity: 0.4;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.marquee-scroll-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
}

/* 当班级数少不滚动时，使班级列表在右侧剩余空间区域水平居中 */
.marquee-scroll-container.is-scroll-centered {
  justify-content: center;
}

.marquee-scroll-text {
  display: flex;
  white-space: nowrap;
}

/* 只有超过 3 个班级时，才激活滚动动画 */
.marquee-scroll-active {
  animation: scroll-left-smooth 25s linear infinite;
}

.marquee-content-inner {
  display: flex;
  flex-shrink: 0;
}

.marquee-item {
  margin-right: 60px;
  font-size: 32px; /* 从 26px 调大到 32px，更具视觉冲击力 */
  font-weight: 800; /* 加粗 */
  color: #ef4444; /* 改为高亮红色 */
}

/* 最后一个元素去除右边距，保证在不滚动居中平铺时整体位置处于绝对正中 */
.is-scroll-centered .marquee-item:last-child {
  margin-right: 0;
}

@keyframes scroll-left-smooth {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-50%, 0, 0);
  }
}

/* 主内容网格区域 */
.main-content-v2 {
  flex: 1;
  padding: 24px 40px;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
}

.class-grid-section {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.grid-scroll-wrapper {
  width: 100%;
}

.grid-scroll-segment {
  width: 100%;
}

/* 滚动循环首尾间隔：约一行卡片高度，避免末尾班级与开头班级贴在一起 */
.grid-loop-gap {
  height: calc(80px + 16px);
  min-height: calc(80px + 16px);
  flex-shrink: 0;
}

.class-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr) !important; /* 修改为每行 6 个格子 */
  gap: 16px;
  grid-auto-rows: min-content;
}

/* ================= 动态颜色系统 ================= */

/* 1. 绿色 (green) */
.color-card-green {
  background: linear-gradient(180deg, #10b981 0%, #059669 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.color-card-green .class-name-v2 {
  color: #ffffff !important;
  text-shadow: 0 2px 4px rgba(4, 120, 87, 0.7);
}
.color-dot-green {
  background: radial-gradient(circle at 35% 35%, #a7f3d0 0%, #10b981 60%, #047857 100%) !important;
  box-shadow: 0 0 12px rgba(16, 185, 129, 0.85) !important;
}
.color-text-green {
  color: #34d399 !important;
  font-weight: 700;
  font-size: 18px;
}
.color-border-green {
  border-color: rgba(16, 185, 129, 0.3) !important;
}
.color-bg-light-green {
  background-color: rgba(16, 185, 129, 0.05) !important;
}
.color-label-green {
  color: #10b981 !important;
  background: rgba(16, 185, 129, 0.08) !important;
  border-color: rgba(16, 185, 129, 0.25) !important;
}

/* 2. 深绿 (darkgreen) */
.color-card-darkgreen {
  background: linear-gradient(180deg, #047857 0%, #064e3b 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.color-card-darkgreen .class-name-v2 {
  color: #ffffff !important;
  text-shadow: 0 2px 4px rgba(2, 44, 34, 0.7);
}
.color-dot-darkgreen {
  background: radial-gradient(circle at 35% 35%, #6ee7b7 0%, #047857 60%, #022c22 100%) !important;
  box-shadow: 0 0 12px rgba(4, 120, 87, 0.85) !important;
}
.color-text-darkgreen {
  color: #059669 !important;
  font-weight: 700;
  font-size: 18px;
}
.color-border-darkgreen {
  border-color: rgba(4, 120, 87, 0.3) !important;
}
.color-bg-light-darkgreen {
  background-color: rgba(4, 120, 87, 0.05) !important;
}
.color-label-darkgreen {
  color: #047857 !important;
  background: rgba(4, 120, 87, 0.08) !important;
  border-color: rgba(4, 120, 87, 0.25) !important;
}

/* 3. 蓝色 (blue) */
.color-card-blue {
  background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.color-card-blue .class-name-v2 {
  color: #ffffff !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}
.color-dot-blue {
  background: radial-gradient(circle at 35% 35%, #bfdbfe 0%, #3b82f6 60%, #1d4ed8 100%) !important;
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.85) !important;
}
.color-text-blue {
  color: #60a5fa !important;
  font-weight: 700;
  font-size: 18px;
}
.color-border-blue {
  border-color: rgba(59, 130, 246, 0.3) !important;
}
.color-bg-light-blue {
  background-color: rgba(59, 130, 246, 0.05) !important;
}
.color-label-blue {
  color: #3b82f6 !important;
  background: rgba(59, 130, 246, 0.08) !important;
  border-color: rgba(59, 130, 246, 0.25) !important;
}

/* 4. 深蓝 (darkblue) */
.color-card-darkblue {
  background: linear-gradient(180deg, #1d4ed8 0%, #1e3a8a 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.color-card-darkblue .class-name-v2 {
  color: #ffffff !important;
  text-shadow: 0 2px 4px rgba(23, 37, 84, 0.7);
}
.color-dot-darkblue {
  background: radial-gradient(circle at 35% 35%, #93c5fd 0%, #1d4ed8 60%, #172554 100%) !important;
  box-shadow: 0 0 12px rgba(29, 78, 216, 0.85) !important;
}
.color-text-darkblue {
  color: #2563eb !important;
  font-weight: 700;
  font-size: 18px;
}
.color-border-darkblue {
  border-color: rgba(29, 78, 216, 0.3) !important;
}
.color-bg-light-darkblue {
  background-color: rgba(29, 78, 216, 0.05) !important;
}
.color-label-darkblue {
  color: #1d4ed8 !important;
  background: rgba(29, 78, 216, 0.08) !important;
  border-color: rgba(29, 78, 216, 0.25) !important;
}

/* 5. 黑色 (black) */
.color-card-black {
  background: linear-gradient(180deg, #334155 0%, #0f172a 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.color-card-black .class-name-v2 {
  color: #ffffff !important;
  text-shadow: 0 2px 4px rgba(15, 23, 42, 0.7);
}
.color-dot-black {
  background: radial-gradient(circle at 35% 35%, #94a3b8 0%, #475569 60%, #0f172a 100%) !important;
  box-shadow: 0 0 12px rgba(71, 85, 105, 0.85) !important;
}
.color-text-black {
  color: #94a3b8 !important;
  font-weight: 700;
  font-size: 18px;
}
.color-border-black {
  border-color: rgba(148, 163, 184, 0.3) !important;
}
.color-bg-light-black {
  background-color: rgba(148, 163, 184, 0.05) !important;
}
.color-label-black {
  color: #94a3b8 !important;
  background: rgba(148, 163, 184, 0.08) !important;
  border-color: rgba(148, 163, 184, 0.25) !important;
}

/* 6. 金黄色 (gold) */
.color-card-gold {
  background: linear-gradient(180deg, #eab308 0%, #ca8a04 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
}
.color-card-gold .class-name-v2 {
  color: #ffffff !important;
  text-shadow: 0 2px 4px rgba(133, 77, 14, 0.7);
}
.color-dot-gold {
  background: radial-gradient(circle at 35% 35%, #fef08a 0%, #eab308 60%, #854d0e 100%) !important;
  box-shadow: 0 0 12px rgba(234, 179, 8, 0.85) !important;
}
.color-text-gold {
  color: #eab308 !important;
  font-weight: 700;
  font-size: 18px;
}
.color-border-gold {
  border-color: rgba(234, 179, 8, 0.3) !important;
}
.color-bg-light-gold {
  background-color: rgba(234, 179, 8, 0.05) !important;
}
.color-label-gold {
  color: #eab308 !important;
  background: rgba(234, 179, 8, 0.08) !important;
  border-color: rgba(234, 179, 8, 0.25) !important;
}

/* 7. 红色 (red) */
.color-card-red {
  background: linear-gradient(180deg, #ef4444 0%, #b91c1c 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
}
.color-card-red .class-name-v2 {
  color: #ffffff !important;
  text-shadow: 0 2px 4px rgba(153, 27, 27, 0.7);
}
.color-dot-red {
  background: radial-gradient(circle at 35% 35%, #fecaca 0%, #ef4444 60%, #991b1b 100%) !important;
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.9) !important;
}
.color-text-red {
  color: #ef4444 !important;
  font-weight: 800;
  font-size: 18px;
}
.color-border-red {
  border-color: rgba(239, 68, 68, 0.3) !important;
}
.color-bg-light-red {
  background-color: rgba(239, 68, 68, 0.05) !important;
}
.color-label-red {
  color: #ef4444 !important;
  background: rgba(239, 68, 68, 0.08) !important;
  border-color: rgba(239, 68, 68, 0.25) !important;
}

/* 班级卡片基础设计：纯扁平圆角卡片，文字直接叠上去 */
.class-card-v2 {
  border-radius: 6px; /* 改为更干练的 6px 圆角以复刻参考图 */
  height: 80px; /* 卡片高度由 120px 调低至 80px，支持单屏展示 7 行卡片，完美复刻参考图 */
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding: 0 16px;
  transition: transform 0.2s ease;
  position: relative;
  overflow: hidden;
  
  /* 彻底剥离 3D 效果，去除顶部高光、底部暗层与悬浮阴影 */
  box-shadow: none;
}

.class-card-v2:hover {
  transform: scale(1.02); /* 悬浮仅作轻微缩放 */
  box-shadow: none;
}

.class-name-v2 {
  font-size: 32px;
  font-weight: 800;
  letter-spacing: 1px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 卡片状态颜色 */

/* 1. 待放学状态 (Status = 0)：明亮蓝色渐变（与图例蓝色统一） */
.card-pending-v2 {
  background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

.card-pending-v2 .class-name-v2 {
  color: #ffffff; /* 白色大字，大屏展示对比度最高 */
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

/* 2. 放学中状态 (Status = 1)：高亮红色渐变 */
.card-dismissing-v2 {
  background: linear-gradient(180deg, #ef4444 0%, #b91c1c 100%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: none; /* 去除 3D 底部暗底与多余发光 */
}

.card-dismissing-v2 .class-name-v2 {
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(153, 27, 27, 0.7);
}

/* 3. 已放学状态 (Status = 2)：立体翡翠绿渐变（与图例绿色统一） */
.card-finished-v2 {
  background: linear-gradient(180deg, #10b981 0%, #059669 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

.card-finished-v2 .class-name-v2 {
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(4, 120, 87, 0.7);
}

/* 暂无数据提示 */
.empty-tip-v2 {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 250px;
  color: #475569;
  font-size: 20px;
  font-weight: 700;
  gap: 15px;
}

.empty-icon {
  width: 50px;
  height: 50px;
  border: 2px dashed #475569;
  border-radius: 50%;
  position: relative;
  opacity: 0.6;
}

.empty-icon::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 2px;
  background-color: #475569;
}

/* 底部状态说明：固定 px 布局，兼容电视机/Android WebView（避免 clamp、gap 失效） */
.bottom-legend-v2 {
  flex-shrink: 0;
  height: 110px;
  min-height: 110px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 40px 16px;
  background: rgba(15, 23, 42, 0.85);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  box-sizing: border-box;
  z-index: 8;
}

.legend-item {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
  margin: 0 56px;
}

.legend-dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 16px;
}

.legend-text {
  font-size: 40px;
  font-weight: 700;
  letter-spacing: 3px;
  line-height: 1.2;
  flex-shrink: 0;
  -webkit-text-size-adjust: 100%;
}

.dot-pending {
  background: radial-gradient(circle at 35% 35%, #93c5fd 0%, #3b82f6 60%, #1d4ed8 100%); /* 径向渐变实现 3D 水晶高光 */
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.85);
}

.dot-dismissing {
  background: radial-gradient(circle at 35% 35%, #fee2e2 0%, #ef4444 60%, #991b1b 100%); /* 径向渐变实现 3D 红色水晶高光 */
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.9);
  animation: dot-pulse 1.2s infinite alternate;
}

.dot-finished {
  background: radial-gradient(circle at 35% 35%, #a7f3d0 0%, #10b981 60%, #047857 100%); /* 径向渐变实现 3D 水晶高光 */
  box-shadow: 0 0 12px rgba(16, 185, 129, 0.85);
}

@keyframes dot-pulse {
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 8px rgba(239, 68, 68, 0.6);
  }
  100% {
    transform: scale(1.1);
    box-shadow: 0 0 18px rgba(239, 68, 68, 1);
  }
}

/* 对应状态颜色同步的 Legend 文字 */
.text-pending {
  color: #60a5fa !important;
  font-weight: 700;
  font-size: 18px;
}

.text-dismissing {
  color: #ef4444 !important;
  font-weight: 800;
  font-size: 18px;
  animation: legend-pulse-red 1.5s infinite alternate;
}

.text-finished {
  color: #34d399 !important;
  font-weight: 700;
  font-size: 18px;
}

@keyframes legend-pulse-red {
  0% {
    opacity: 0.85;
    text-shadow: 0 0 2px rgba(239, 68, 68, 0.2);
  }
  100% {
    opacity: 1;
    text-shadow: 0 0 6px rgba(239, 68, 68, 0.5);
  }
}

/* 霸屏大字组件容器 */
.overlay-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

/* 深色半透明蒙层 */
/* 纯透明蒙层，不调暗不模糊，让背景文字完全清晰可见 */
.overlay-backdrop {
  position: absolute;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 1;
}

.overlay-content-box {
  position: relative;
  z-index: 2;
  width: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 霸屏超大字体，红色 */
.overlay-class-name {
  font-size: 17vw; /* 大幅调大字号以基本撑满左右全屏，复刻截图效果 */
  font-weight: 900;
  color: #ef4444;
  letter-spacing: 6px;
  text-align: center;
  white-space: nowrap; /* 保证超大文字只在单行显示，绝不折行 */
  text-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.8),
    0 0 45px rgba(239, 68, 68, 0.85);
  /* 保持静止，不做多余跳动 */
}

/* 霸屏轻微放大淡入淡出 */
.overlay-scale-enter-active,
.overlay-scale-leave-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.overlay-scale-enter {
  opacity: 0;
  transform: scale(0.75);
}

.overlay-scale-leave-to {
  opacity: 0;
  transform: scale(1.08);
}
.dot-pulse-dynamic {
  animation: dot-pulse-var 1.2s infinite alternate;
}
@keyframes dot-pulse-var {
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 8px rgba(var(--pulse-rgb, 239, 68, 68), 0.6);
  }
  100% {
    transform: scale(1.1);
    box-shadow: 0 0 18px rgba(var(--pulse-rgb, 239, 68, 68), 1);
  }
}
@keyframes beacon-blink-var {
  0% {
    opacity: 0.4;
    transform: scale(0.9);
    box-shadow: 0 0 4px rgba(var(--pulse-rgb, 239, 68, 68), 0.4);
  }
  100% {
    opacity: 1;
    transform: scale(1.1);
    box-shadow: 0 0 10px rgba(var(--pulse-rgb, 239, 68, 68), 0.9);
  }
}

</style>
